import React from "react";
import { ArrowLeft, Shield, FileText, Users, Heart, Globe } from "lucide-react";

const TermsAndConditions = () => {
  const handleBackToHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFEBB5] to-[#FFF8E6]">
      {/* Header */}
      <div className="bg-[#67142D] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-source-serif-4 font-bold">Terms and Conditions</h1>
              <p className="text-lg mt-2 font-inter">
                Vishwa Sanatan Mahapeeth - Terms of Service
              </p>
            </div>
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="mb-8">
            <p className="text-[#67142D] font-semibold mb-4">
              <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-IN', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p className="text-[#67142D]/80 font-inter leading-relaxed">
              Welcome to Vishwa Sanatan Mahapeeth. These Terms & Conditions ("Terms") govern your access to and use of our website www.sanatanmahapeeth.org ("Website"), as well as your participation in our activities, donations, memberships, and volunteer programs. By using this Website or engaging with Vishwa Sanatan Mahapeeth, you agree to abide by these Terms. If you do not agree, please refrain from using our Website and services.
            </p>
          </div>

          {/* Section 1 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6" />
              1. General Terms
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                Vishwa Sanatan Mahapeeth is a spiritual organization committed to religious, social, environmental, and humanitarian activities, including:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Protection and promotion of Sanatan Dharma</li>
                <li>Spiritual awakening and Vedic knowledge dissemination</li>
                <li>Community service, environmental conservation, and cultural preservation</li>
                <li>Educational programs and skill development</li>
                <li>Support for spiritual seekers and the underprivileged</li>
                <li>Preservation and restoration of spiritual heritage</li>
              </ul>
              <p>
                These Terms govern your interactions with Vishwa Sanatan Mahapeeth, including donations, membership fees, financial contributions, volunteer participation, community programs, use of our Website and digital platforms, and any other engagement with the organization's activities.
              </p>
              <p>
                We reserve the right to modify, update, or discontinue any part of these Terms at any time without prior notice.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4 flex items-center gap-3">
              <Users className="w-6 h-6" />
              2. Membership, Volunteer Participation & Responsibilities
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                Individuals who register as members or volunteers agree to support the mission of Vishwa Sanatan Mahapeeth and abide by the organization's rules and guidelines.
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Membership or volunteer registration does not create any legal rights, ownership, employment, or entitlement to benefits, compensation, or financial assistance.</li>
                <li>The organization reserves the sole discretion to accept or reject membership or volunteer applications without providing a reason.</li>
                <li>Volunteers and members must conduct themselves with honesty, integrity, and discipline while engaging in organization activities.</li>
                <li>Any misuse, misrepresentation, or violation of the organization's principles may result in immediate suspension or termination of membership.</li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4 flex items-center gap-3">
              <Heart className="w-6 h-6" />
              3. Donations, Financial Contributions & Usage
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                All donations, membership fees, and financial contributions made to Vishwa Sanatan Mahapeeth are voluntary, non-refundable, and non-transferable.
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Donations are used solely for the objectives of the organization, which include religious, social, environmental, and humanitarian activities.</li>
                <li>Donors and contributors cannot claim any rights, control, or authority over the funds once donated.</li>
                <li>Donations may be accepted via online payment gateways, bank transfers, cash, cheques, or other approved methods.</li>
                <li>Donors and members cannot demand a refund, reimbursement, or claim ownership over their contributions.</li>
                <li>Vishwa Sanatan Mahapeeth reserves the right to use donations at its discretion, ensuring alignment with its objectives and charitable commitments.</li>
                <li>No donation or contribution entitles any donor to influence the organization's policies, decisions, or functioning.</li>
              </ul>
            </div>
          </div>

          {/* Section 4 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4 flex items-center gap-3">
              <Globe className="w-6 h-6" />
              4. Events, Spiritual Services & Cultural Programs
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                Vishwa Sanatan Mahapeeth organizes religious, cultural, and community events, including:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Spiritual discourses and religious ceremonies</li>
                <li>Yajna Samagams and cultural programs</li>
                <li>Environmental and social welfare programs</li>
                <li>Educational workshops and spiritual retreats</li>
              </ul>
              <p>
                Participation in any event is at the sole discretion of the organization. The organization is not responsible for any health issues, allergies, or inconveniences arising from food, accommodation, or participation in its programs. Event details, schedules, and services are subject to change without prior notice.
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4 flex items-center gap-3">
              <FileText className="w-6 h-6" />
              5. Website Usage & Digital Content
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                All content on www.sanatanmahapeeth.org, including text, images, videos, logos, and graphics, is the intellectual property of Vishwa Sanatan Mahapeeth.
              </p>
              <p>
                Users are strictly prohibited from:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Copying, modifying, distributing, or reproducing content without permission</li>
                <li>Misusing the Website for fraudulent or illegal activities</li>
                <li>Posting misleading, harmful, or offensive material</li>
                <li>Attempting to hack, manipulate, or disrupt the Website's operations</li>
              </ul>
              <p>
                Any violation of these terms may result in legal action and permanent user restrictions. The Website may contain third-party links, but Vishwa Sanatan Mahapeeth is not responsible for their content, security, or policies.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              6. Payment Gateway & Financial Transactions
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                Vishwa Sanatan Mahapeeth integrates secure online payment gateways for donations and contributions. Users agree to abide by the respective payment processor's terms and conditions.
              </p>
              <p>
                The organization is not responsible for technical failures, cyber threats, online transaction errors, payment delays, banking issues, or disputes with financial institutions. Users must ensure their payment details are accurate, and any false information may lead to legal consequences.
              </p>
            </div>
          </div>

          {/* Section 7 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              7. Limitation of Liability
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                Vishwa Sanatan Mahapeeth, its trustees, members, volunteers, and employees cannot be held legally liable for:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Any loss, damage, or inconvenience caused by the use of this Website or services</li>
                <li>Any claims related to donations, events, or spiritual services</li>
                <li>Any indirect, incidental, or consequential damages arising from interactions with the organization</li>
              </ul>
              <p>
                The organization provides services on a best-effort basis, and participation is purely voluntary. The organization reserves the right to modify, suspend, or discontinue any service or program without liability.
              </p>
            </div>
          </div>

          {/* Section 8 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              8. Legal Jurisdiction & Dispute Resolution
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                All legal disputes related to Vishwa Sanatan Mahapeeth, its activities, donations, or services shall be subject to the exclusive jurisdiction of the courts in Haridwar, Uttarakhand, India.
              </p>
              <p>
                No legal action can be taken against the organization, its trustees, office bearers, or volunteers for any decision made in good faith. In case of any disputes, parties must first attempt to resolve the issue through discussion or mediation before taking legal steps.
              </p>
            </div>
          </div>

          {/* Section 9 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              9. Amendments to Terms & Conditions
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                Vishwa Sanatan Mahapeeth reserves the right to modify, update, or change these Terms & Conditions at any time without prior notice. Any changes will be posted on this page with an updated "Last Updated" date. Continued use of the Website and services after modifications constitutes acceptance of the revised Terms.
              </p>
            </div>
          </div>

          {/* Section 10 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              10. Contact Information
            </h2>
            <div className="bg-[#67142D]/5 p-6 rounded-lg">
              <div className="space-y-3 text-[#67142D] font-inter">
                <p><strong>📍 Vishwa Sanatan Mahapeeth</strong></p>
                <p><strong>🌐 Website:</strong> www.sanatanmahapeeth.org</p>
                <p><strong>📧 Email:</strong> info@sanatanmahapeeth.org</p>
                <p><strong>📞 Phone:</strong> +91-9520050068 / +91-9520050038</p>
                <p><strong>📍 Address:</strong> तीर्थ आश्रम, शिवलोक कॉलोनी, भगत सिंह चौराहे के पास, हरिद्वार, उत्तराखंड - 249401</p>
              </div>
            </div>
          </div>

          {/* Section 11 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              11. Consent & Acceptance
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                By using our Website, making a donation, registering as a member/volunteer, or participating in our activities, you acknowledge that you have read, understood, and agreed to these Terms & Conditions.
              </p>
              <p className="font-semibold text-[#67142D]">
                These Terms & Conditions ensure complete transparency, legal clarity, and protection for all individuals engaging with Vishwa Sanatan Mahapeeth.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-[#67142D]/20 pt-8 mt-8">
            <div className="text-center">
              <p className="text-[#67142D]/60 font-inter text-sm">
                © 2025 Vishwa Sanatan Mahapeeth. All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
